import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select'
import moment from 'moment';
import useAccounts from '../hooks/useAccounts';
import * as XLSX from 'xlsx';
import 'moment/locale/es'
import { useTranslation } from 'react-i18next';
import Loader from './Loader';
declare const HSCore: any;
declare const $: any;

interface ColumnType {
    name: string,
    sortable?: Boolean,
    center?: Boolean,
    exportable?: Boolean, // If is false, this column will not be exported
    selector?: (row: any) => any, // Required if column is exportable
    formatToExport?: (row: any) => any, // Required for custom string export
    cell?: (row: any) => React.ReactNode,
    width?: number,
}

interface AccountType {
    value: string,
    label: Boolean,
}

interface optionsExtraFilter {
    value: string,
    label: string,
}

interface CustomDataTableProps {
    columns: Array<ColumnType>;
    data: Array<any>;
    filters?: Array<React.ReactNode>;
    showDates?: boolean;
    showExport?: boolean;
    onExport?: (format: 'csv' | 'excel') => void;
    extraExportActions?: React.ReactNode;
    singleAccounts?: boolean;
    filter?: (row: any, term: string) => any;
    onAccountsSelected?: (accounts) => any;
    onDatesChange?: (dates) => any;
    accountsDefaultValue?: AccountType | Array<AccountType>,
    hideAccountsFilter?: Boolean,
    isLoading?: Boolean,
    extraFilter?: optionsExtraFilter | Array<optionsExtraFilter>,
    filterDay?: number,
    onRowsFiltered?: (filteredRows: any[]) => void;
    onRowsSelected?: any;
    rowIdPath?: string;
}

function CustomDataTable(props: CustomDataTableProps) {
    const { t } = useTranslation();

    const rangeElement = useRef(null)

    const { accounts } = useAccounts();

    const [hideColumns, setHideColumns] = useState({})
    const [columns, setColumns] = useState(props.columns)
    const [rows, setRows] = useState(props.data)
    const [termFilter, setTermFilter] = useState('')
    const [term, setTerm] = useState('')
    const daysToSubtract = props.filterDay ? props.filterDay : 29;
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [rangeFilter, setRangeFilter] = useState({
        start: props.filterDay === 0 ? moment() : moment().subtract(daysToSubtract, 'days'),
        end: moment(),
    });

    const [paginationConfig, setPaginationConfig] = useState({
        totalPages: 1,
        currentPage: 0,
        rowsPerPage: 20,
        isFirst: true,
        isLast: false,
        rows: []
    })

    useEffect(() => {
        if (!rangeElement || !rangeElement.current) return;

        $(rangeElement.current).find('.js-daterangepicker-predefined-preview').html(rangeFilter.start.format('MMMM D, YYYY') + ' - ' + rangeFilter.end.format('MMMM D, YYYY'));

        const cb = (start, end) => {
            setRangeFilter({
                start,
                end
            })

            $(rangeElement.current).find('.js-daterangepicker-predefined-preview').html(start.format('MMMM D, YYYY') + ' - ' + end.format('MMMM D, YYYY'));
        }

        $(rangeElement.current).daterangepicker({
            startDate: rangeFilter.start,
            endDate: rangeFilter.end,
            ranges: {
                [t('date-time.today')]: [moment(), moment()],
                [t('date-time.yesterday')]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                [t('date-time.last-days-7')]: [moment().subtract(6, 'days'), moment()],
                [t('date-time.last-days-30')]: [moment().subtract(29, 'days'), moment()],
                [t('date-time.current-month')]: [moment().startOf('month'), moment().endOf('month')],
                [t('date-time.last-month')]: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
            },
            locale: {
                applyLabel: t('apply-btn'),
                cancelLabel: t('btn-cancel'),
                customRangeLabel: t('date-time.personalized'),
                daysOfWeek: [
                    t('date-time.sun'),
                    t('date-time.mon'),
                    t('date-time.tue'),
                    t('date-time.wed'),
                    t('date-time.thu'),
                    t('date-time.fri'),
                    t('date-time.sat')
                ],
                monthNames: [
                    t('date-time.jan'),
                    t('date-time.feb'),
                    t('date-time.mar'),
                    t('date-time.apr'),
                    t('date-time.may'),
                    t('date-time.jun'),
                    t('date-time.jul'),
                    t('date-time.aug'),
                    t('date-time.sep'),
                    t('date-time.oct'),
                    t('date-time.nov'),
                    t('date-time.dec'),
                ],
            }
        }, cb);

    }, [rangeElement])

    useEffect(() => {
        if (props.onDatesChange) {
            props.onDatesChange(rangeFilter)
        }

    }, [rangeFilter])

    useEffect(() => {
        setColumns(props.columns)
        setRows(props.data)
    }, [props.columns, props.data])


    const columnsFiltered = useMemo(() => {
        return columns.filter((_, index) => !hideColumns[index])
    }, [columns, hideColumns])

    const getRowId = (row: any): string => {
        if (!props.rowIdPath) return row.id;
        return props.rowIdPath.split('.').reduce((acc, key) => acc?.[key], row);
    };

    const toggleRowSelection = (row: any) => {
        const id = getRowId(row);
        const updated = selectedRows.includes(id)
            ? selectedRows.filter(item => item !== id)
            : [...selectedRows, id];

        const allSelectedNow = updated.length === filteredRows.length;
        setSelectedRows(updated);
        setAllSelected(allSelectedNow);

        if (props.onRowsSelected) props.onRowsSelected({ ids: updated, allSelected: allSelectedNow });
    };

    const toggleAllRows = () => {
        if (allSelected) {
            setSelectedRows([]);
            setAllSelected(false);
            if (props.onRowsSelected) props.onRowsSelected({ ids: [], allSelected: false });
        } else {
            const allIds = filteredRows.map(r => getRowId(r));
            setSelectedRows(allIds);
            setAllSelected(true);
            if (props.onRowsSelected) props.onRowsSelected({ ids: allIds, allSelected: true });
        }
    };

    const getHeaders = useMemo(() => {
        const headers = [];

        if (props.onRowsSelected && rows.length > 0) {
            headers.push(
                <th key="select-all" className="text-center">
                    <div className="checkbox-wrapper-12">
                        <div className="cbx">
                            <input defaultChecked id="cbx-12" type="checkbox"
                                checked={allSelected}
                                onChange={toggleAllRows} />
                            <label htmlFor="cbx-12" />
                            <svg fill="none" height="14" viewBox="0 0 13 20" width="10">
                                <path d="M2 8.36364L6.23077 12L13 2" />
                            </svg>
                        </div>
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <filter id="goo-12">
                                    <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="4" />
                                    <feColorMatrix
                                        in="blur"
                                        mode="matrix"
                                        result="goo-12"
                                        values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -7"
                                    />
                                    <feBlend in="SourceGraphic" in2="goo-12" />
                                </filter>
                            </defs>
                        </svg>
                    </div>
                </th>
            );
        }

        headers.push(
            ...columnsFiltered.map((column, index) => (
                <th
                    scope="col"
                    key={index}
                    className={column.center ? 'text-center text-white-dark' : 'text-white-dark'}
                    style={{ width: column.width ? `${column.width}%` : 'auto' }}
                >
                    {column.name}
                </th>
            ))
        );

        return headers;
    }, [columnsFiltered, allSelected, props.onRowsSelected]);


    const isScrollable = getHeaders.length > 8;

    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            const termMatch = term ? props.filter(row, term) : true;
            const termFilterMatch = termFilter ? props.filter(row, termFilter) : true;
            return termMatch && termFilterMatch;
        });
    }, [rows, term, termFilter, props.filter]);


    useEffect(() => {
        if (props.onRowsFiltered) {
            props.onRowsFiltered(filteredRows);
        }
    }, [filteredRows, props.onRowsFiltered]);

    const rowsOfTable = useMemo(() => {
        return filteredRows.map((row, index) => {
            const rowId = getRowId(row);
            return (
                <tr key={index}>
                    {props.onRowsSelected && (
                        <td className="text-center">
                            <div className="checkbox-wrapper-12">
                                <div className="cbx">
                                    <input
                                        type="checkbox"
                                        id={`cbx-row-${index}`}
                                        checked={selectedRows.includes(rowId)}
                                        onChange={() => toggleRowSelection(row)}
                                    />
                                    <label htmlFor={`cbx-row-${index}`} />
                                    <svg fill="none" height="14" viewBox="0 0 13 20" width="10">
                                        <path d="M2 8.36364L6.23077 12L13 2" />
                                    </svg>
                                </div>
                                <svg version="1.1" xmlns="http://www.w3.org/2000/svg">
                                    <defs>
                                        <filter id="goo-12">
                                            <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="4" />
                                            <feColorMatrix
                                                in="blur"
                                                mode="matrix"
                                                result="goo-12"
                                                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -7"
                                            />
                                            <feBlend in="SourceGraphic" in2="goo-12" />
                                        </filter>
                                    </defs>
                                </svg>
                            </div>
                        </td>
                    )}
                    {columnsFiltered.map((column, indexCol) => (
                        <td key={`row-${index}-col-${indexCol}`} className={column.center ? 'text-center' : ''}>
                            {column.cell ? column.cell(row) : column.selector(row)}
                        </td>
                    ))}
                </tr>
            );
        });
    }, [filteredRows, columnsFiltered, selectedRows, props.onRowsSelected]);

    const handleHideColumns = (index, checked) => {
        setHideColumns({ ...hideColumns, [index]: !checked })
    }

    const getMin = (val1, val2) => {
        return Math.min(val1, val2)
    }

    const handleNextPrevPage = (action: 'prev' | 'next' | 'first' | 'last') => {
        let currentPage = 0;

        if (action === 'prev') {
            currentPage = paginationConfig.currentPage - 1;
        } else if (action === 'next') {
            currentPage = paginationConfig.currentPage + 1;
        } else if (action === 'first') {
            currentPage = 0;
        } else if (action === 'last') {
            currentPage = paginationConfig.totalPages - 1;
        }

        let isFirst, isLast;

        if (currentPage < 0) {
            currentPage = 0;
            isFirst = true;
            isLast = false;
        }

        if (currentPage >= paginationConfig.totalPages - 1) {
            currentPage = paginationConfig.totalPages - 1;

            isFirst = false;
            isLast = true;
        }

        const start = (currentPage * paginationConfig.rowsPerPage);

        const rows = rowsOfTable.slice(start, start + paginationConfig.rowsPerPage)

        setPaginationConfig({ ...paginationConfig, currentPage, rows, isFirst, isLast })
    }

    const exportToExcel = () => {
        const HEADER = columns.filter(c => c.exportable !== false);

        let data = []
        rows.forEach(row => {
            let $row = {}
            HEADER.forEach(h => $row[h.name] = h.formatToExport ? h.formatToExport(row) : h.selector(row) || '-')

            data.push($row)
        })

        const wb = XLSX.utils.book_new();

        const ws = XLSX.utils.json_to_sheet(data, {
            header: HEADER.map(c => c.name)
        });

        XLSX.utils.book_append_sheet(wb, ws);

        return XLSX.writeFile(wb, `${new Date().getTime()}.xlsx`);
    }

    const downloadCSV = () => {
        const link = document.createElement('a');
        let csv = convertArrayOfObjectsToCSV();
        if (csv == null) return;

        const filename = `${new Date().getTime()}.csv`;

        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }

        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', filename);
        link.click();
    }

    // Blatant "inspiration" from https://codepen.io/Jacqueline34/pen/pyVoWr
    const convertArrayOfObjectsToCSV = () => {
        let result;

        const columnDelimiter = ',';
        const lineDelimiter = '\n';
        const keys = columns.filter(c => c.exportable !== false).map(c => c.name);

        result = '';
        result += keys.join(columnDelimiter);
        result += lineDelimiter;

        let data: any = rows.map(row => columns.filter(c => c.exportable !== false).map(c => c.formatToExport ? `"${c.formatToExport(row)}"` : `"${c.selector(row) || '-'}"`))
        result += data.map(d => d.join(columnDelimiter)).join(lineDelimiter)


        return result;
    }


    useEffect(() => {
        const total = rowsOfTable.length;

        const totalPages = Math.ceil(total / paginationConfig.rowsPerPage)

        setPaginationConfig({
            ...paginationConfig,
            totalPages: totalPages,
            currentPage: 0,
            rows: rowsOfTable.slice(0, paginationConfig.rowsPerPage),
            isFirst: true,
            isLast: totalPages === 1,
        })
    }, [rowsOfTable, paginationConfig.rowsPerPage])


    return (
        <div className="card">


            <div className="card-header card-header-content-md-between">

                <div className="mb-2 mb-md-0" >
                    <div className="input-group input-group-merge ">
                        <div className="input-group-prepend input-group-text">
                            <i className="bi-search"></i>
                        </div>
                        <input type="search" className="form-control" placeholder={t("components.custom-table.filter")} value={term} onChange={({ target: { value } }) => setTerm(value)} />
                    </div>
                </div>

                <div className="d-grid d-sm-flex justify-content-md-end align-items-sm-center gap-2">
                    {
                        props.extraFilter && (
                            <div className='mb-2 mb-md-0'>
                                <Select
                                    options={props.extraFilter as optionsExtraFilter[]}
                                    className={'form-select-custom width-select'}
                                    classNamePrefix='form-select-custom'
                                    placeholder={"Selecciona una opción"}
                                    onChange={(option) => setTermFilter(option ? option.value : '')}
                                    isClearable
                                />
                            </div>
                        )
                    }
                    {
                        props.showDates && (
                            <button type="button" title="calendar" ref={rangeElement} className="btn bg-info-alternative text-white btn-sm">
                                <i className="bi-calendar-week me-1"></i>
                                <span className="js-daterangepicker-predefined-preview"></span>
                            </button>
                        )
                    }

                    {
                        !props.hideAccountsFilter && (
                            <div className='mb-2 mb-md-0'>
                                <Select
                                    isMulti={!props.singleAccounts}
                                    options={accounts.map(a => ({ value: a.account_id, label: a.nombre }))}
                                    className={'form-select-custom width-select'}
                                    classNamePrefix='form-select-custom'
                                    placeholder={t("components.custom-table.account-placeholder")}
                                    onChange={(value) => props.onAccountsSelected ? props.onAccountsSelected(value) : () => { }}
                                    defaultValue={props.accountsDefaultValue || []}
                                />
                            </div>
                        )
                    }


                    {/* <div className="dropdown">
                        <button type="button" className="btn bg-soft-info-alternative btn-sm w-100" id="usersFilterDropdown" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <i className="bi-filter me-1"></i> Filtros <span className="badge bg-soft-dark text-dark rounded-circle ms-1">1</span>
                        </button>

                        <div className="dropdown-menu dropdown-menu-sm-end dropdown-card card-dropdown-filter-centered" aria-labelledby="usersFilterDropdown" style={{ minWidth: '22rem' }}>

                            <div className="card">
                                <div className="card-header card-header-content-between">
                                    <h5 className="card-header-title">Filtrar por</h5>
                                    <button type="button" className="btn btn-ghost-secondary btn-icon btn-sm ms-2">
                                        <i className="bi-x-lg"></i>
                                    </button>
                                </div>

                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label" >Ticket</label>
                                        <input type="text" className="form-control" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label" >Número a 10 dígitos</label>
                                        <input type="text" className="form-control" />
                                    </div>
                                    <div className='mb-3'>
                                        <label className="form-label" >Categorías </label>
                                        <Select
                                            isMulti
                                            name="colors"
                                            options={[
                                                { value: 'chocolate', label: 'Chocolate' },
                                                { value: 'strawberry', label: 'Strawberry' },
                                                { value: 'vanilla', label: 'Vanilla' }
                                            ]}
                                            className={false ? 'form-select-custom is-invalid' : ''}
                                            classNamePrefix='form-select-custom'
                                            placeholder='Categorías'
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <label className="form-label" >Cuentas</label>
                                        <Select
                                            isMulti
                                            name="colors"
                                            options={[
                                                { value: 'chocolate', label: 'Chocolate' },
                                                { value: 'strawberry', label: 'Strawberry' },
                                                { value: 'vanilla', label: 'Vanilla' }
                                            ]}
                                            className={false ? 'form-select-custom is-invalid' : ''}
                                            classNamePrefix='form-select-custom'
                                            placeholder='Cuentas'
                                        />
                                    </div>

                                    <hr />

                                    <div className="d-grid ">
                                        <button className="btn btn-primary">Aplicar</button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div> */}

                    {/* <div className="dropdown">
                        <button type="button" className="btn bg-soft-info-alternative btn-sm dropdown-toggle w-100 w-100" data-bs-toggle="dropdown" aria-expanded="true">
                            <i className="bi-download me-1"></i> Descargar conversaciones
                        </button>

                        <div className="dropdown-menu dropdown-menu-sm-end " >
                            <button 
                                className="dropdown-item" 
                                onClick={() => props.onExport('excel')}
                            >
                                <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/brands/excel-icon.svg" alt="Image Description" />
                                Excel
                            </button>
                            <button className="dropdown-item" onClick={() => props.onExport('csv')}>
                                <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/components/placeholder-csv-format.svg" alt="Image Description" />
                                .CSV
                            </button>
                        </div>
                    </div> */}

                    {
                        props.showExport && (
                            <div className="dropdown">
                                <button type="button" className="btn bg-soft-info-alternative btn-sm dropdown-toggle w-100 w-100 text-white-dark" data-bs-toggle="dropdown" aria-expanded="true">
                                    <i className="bi-download me-1 text-primary fw-bold"></i> {t("components.custom-table.export")}
                                </button>

                                <div className="dropdown-menu dropdown-menu-sm-end " >
                                    <button
                                        className="dropdown-item"
                                        onClick={exportToExcel}
                                    >
                                        <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/brands/excel-icon.svg" alt="Image Description" />
                                        Excel
                                    </button>
                                    <button className="dropdown-item" onClick={downloadCSV}>
                                        <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/components/placeholder-csv-format.svg" alt="Image Description" />
                                        .CSV
                                    </button>
                                </div>
                            </div>
                        )
                    }
                    {
                        props.onExport && (
                            <div className="dropdown">
                                <button type="button" className="btn bg-soft-info-alternative btn-sm dropdown-toggle w-100 text-white-dark" data-bs-toggle="dropdown" aria-expanded="true">
                                    <i className="bi-download me-1 text-primary fw-bolder"></i> {t("components.custom-table.export")}
                                </button>

                                <div className="dropdown-menu dropdown-menu-sm-end " >
                                    <button
                                        className="dropdown-item"
                                        onClick={() => props.onExport('excel')}
                                    >
                                        <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/brands/excel-icon.svg" alt="Image Description" />
                                        Excel
                                    </button>
                                    <button className="dropdown-item" onClick={() => props.onExport('csv')}>
                                        <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/components/placeholder-csv-format.svg" alt="Image Description" />
                                        .CSV
                                    </button>

                                    {
                                        props.extraExportActions && (
                                            <>
                                                <div className="dropdown-divider"></div>
                                                {props.extraExportActions}
                                            </>
                                        )
                                    }

                                </div>
                            </div>
                        )
                    }

                    <div className="dropdown">
                        <button type="button" className="btn bg-soft-info-alternative btn-sm w-100 text-white-dark" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <i className="bi-table me-1 text-primary fw-bolder"></i> {t("components.custom-table.columns")}
                        </button>

                        <div className="dropdown-menu dropdown-menu-sm-end dropdown-card card-dropdown-filter-centered" style={{ minWidth: '15rem' }}>

                            <div className="card">
                                <div className='card-body'>
                                    <div className='d-grid gap-3'>
                                        {
                                            columns.map((c, index) => (
                                                <label className="row form-check form-switch" htmlFor={`toggleColumn_order_${index}`} key={index}>
                                                    <span className="col-8 col-sm-9 ms-0"><span className="me-2">{c.name}</span> </span>
                                                    <span className="col-4 col-sm-3 text-end">
                                                        <input type="checkbox" className="form-check-input" id={`toggleColumn_order_${index}`} checked={!hideColumns[index]} onChange={({ target: { checked } }) => handleHideColumns(index, checked)} />
                                                    </span>
                                                </label>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
            <div className='relative'>
                <div className={isScrollable ? 'table-wrapper' : ''}>
                    <div className={`table-responsive ${isScrollable ? 'custom-responsive' : 'over-drop'}`}>
                        <table className={`table ${isScrollable ? 'datatable-custom' : ''}`}>
                            <thead className="thead-light">
                                <tr>{getHeaders}</tr>
                            </thead>
                            <tbody>{paginationConfig.rows}</tbody>
                        </table>

                        {rows.length === 0 && (
                            <div className="p-5">
                                <div className="alert alert-soft-dark my-5" role="alert">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <img
                                                className="avatar avatar-xl"
                                                src="/assets/svg/illustrations/oc-browse.svg"
                                                alt="Image Description"
                                                data-hs-theme-appearance="default"
                                            />
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h3 className="alert-heading mb-1">{t("components.custom-table.no-info")}</h3>
                                            <p className="mb-0">{t("components.custom-table.no-info-show")}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* { true && <Loader /> } */}
                {props.isLoading && <Loader />}
            </div>


            {
                rows.length > 0 && (
                    <nav className='p-4 d-flex justify-content-end'>
                        <div className='d-flex align-items-center '>
                            <small className="text-muted">{t("components.custom-table.pages")}</small>

                            <select title="filter" className="form-select form-select-sm w-auto ms-4" onChange={({ target: { value } }) => setPaginationConfig({ ...paginationConfig, rowsPerPage: +value })} defaultValue={paginationConfig.rowsPerPage}>
                                <option value="10">10</option>
                                <option value="15">15</option>
                                <option value="20">20</option>
                                <option value="25">25</option>
                                <option value="30">30</option>
                            </select>

                            <small className="text-muted mx-4">
                                {(paginationConfig.currentPage * paginationConfig.rowsPerPage) + 1} - {getMin(((paginationConfig.currentPage * paginationConfig.rowsPerPage) + 1) + (paginationConfig.rowsPerPage - 1), rowsOfTable.length)} {t("components.custom-table.of")} {rowsOfTable.length}
                            </small>

                            <button title="pagination" type="button" className="btn bg-soft-info-alternative text-white-dark btn-icon" disabled={paginationConfig.isFirst} onClick={() => handleNextPrevPage('first')}><i className="bi bi-chevron-bar-left fw-bolder"></i></button>
                            <button title="pagination" type="button" className="btn bg-soft-info-alternative text-white-dark btn-icon mx-1" disabled={paginationConfig.isFirst} onClick={() => handleNextPrevPage('prev')}><i className="bi bi-chevron-left fw-bolder"></i></button>
                            <button title="pagination" type="button" className="btn bg-soft-info-alternative text-white-dark btn-icon mx-1" disabled={paginationConfig.isLast} onClick={() => handleNextPrevPage('next')}><i className="bi bi-chevron-right fw-bolder"></i></button>
                            <button title="pagination" type="button" className="btn bg-soft-info-alternative text-white-dark btn-icon" disabled={paginationConfig.isLast} onClick={() => handleNextPrevPage('last')}><i className="bi bi-chevron-bar-right fw-bolder"></i></button>
                        </div>
                    </nav>
                )
            }

        </div>
    )
}
export default CustomDataTable